import { UseCaseUsageStats } from "lib/models/user-insights-models";
import { CSRFService } from "lib/services/csrf-service";
import { UserInsightsService } from "lib/services/user-insights-service";
import { useCallback, useEffect, useMemo, useState } from "react";

interface UseUseCaseUsedResult {
  hasUsed: boolean;
  usedCount: number;
  isLoading: boolean;
  markUsed: () => Promise<void>;
}

export function useUseCaseUsed(useCaseId: string): UseUseCaseUsedResult {
  const [hasUsed, setHasUsed] = useState(false);
  const [usedCount, setUsedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const service = useMemo(() => new UserInsightsService(), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  useEffect(() => {
    if (!useCaseId) return;
    service
      .getStats(useCaseId)
      .then((stats: UseCaseUsageStats) => {
        setHasUsed(stats.hasCurrentUserUsed);
        setUsedCount(stats.usedCount);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [useCaseId]);

  const markUsed = useCallback(async () => {
    if (hasUsed) return;
    const csrfToken = await csrfService.getToken();
    const stats = await service.recordUsed(useCaseId, csrfToken);
    setHasUsed(stats.hasCurrentUserUsed);
    setUsedCount(stats.usedCount);
  }, [useCaseId, hasUsed]);

  return { hasUsed, usedCount, isLoading, markUsed };
}
